import { Serializable } from '@/models/core/serializable';
import moment from 'moment';
import { store } from '@/store';

export class StudentCompetencyData implements Serializable<StudentCompetencyData> {
  competencyComparisons: StudentCompetencyComparison[] = []; // 直近のスコアとの変動値(変動幅の絶対値が大きい順)
  highCompetency: StudentCompetencyDistribution = new StudentCompetencyDistribution();
  lowCompetency: StudentCompetencyDistribution = new StudentCompetencyDistribution();
  potentialCompetency: StudentCompetencyDistribution = new StudentCompetencyDistribution();
  proposalPDF: string;
  competencyProposals: StudentCompetencyProposal[] = [];

  deserialize(input: any): StudentCompetencyData {
    this.competencyComparisons = input.competencies.map((studentCompetencyComparison: StudentCompetencyComparison) => new StudentCompetencyComparison().deserialize(studentCompetencyComparison));
    this.highCompetency = new StudentCompetencyDistribution().deserialize(input.highCompetency);
    this.lowCompetency = new StudentCompetencyDistribution().deserialize(input.lowCompetency);
    this.potentialCompetency = new StudentCompetencyDistribution().deserialize(input.potentialCompetency);
    this.proposalPDF = input.proposalPDF;
    this.competencyProposals = input.competencyProposals.map((studentCompetencyProposal: StudentCompetencyProposal) => new StudentCompetencyProposal().deserialize(studentCompetencyProposal));
    return this;
  }

  get maxCount(): number {
    return Math.max.apply(null, [this.highCompetency.maxCount, this.lowCompetency.maxCount, this.potentialCompetency.maxCount]);
  }

  get isShowScore(): boolean {
    //最も低いコンピテンシーと最も高いコンピテンシーが存在する時のみ表示
    return this.highCompetency.competencyID !== 0 && this.lowCompetency.competencyID !== 0;
  }

  get highCompetencyProposal(): string {
    return this.highCompetency.competencyProposal;
  }

  get lowCompetencyProposal(): string {
    return this.lowCompetency.competencyProposal;
  }

  get potentialProposal(): string {
    return this.potentialCompetency.competencyProposal;
  }

  get studentHighCompetencyScore(): number {
    return this.highCompetency.studentCompetencyScore;
  }

  get schoolClassHighCompetencyScore(): number {
    return this.highCompetency.overallCompetencyScore;
  }

  get highCompetencyName(): string {
    return this.highCompetency.competencyName;
  }

  get studentLowCompetencyScore(): number {
    return this.lowCompetency.studentCompetencyScore;
  }

  get schoolClassLowCompetencyScore(): number {
    return this.lowCompetency.overallCompetencyScore;
  }

  get lowCompetencyName(): string {
    return this.lowCompetency.competencyName;
  }

  get studentPotencialCompetencyScore(): number {
    return this.potentialCompetency.studentCompetencyScore;
  }

  get schoolClassPotencialCompetencyScore(): number {
    return this.potentialCompetency.overallCompetencyScore;
  }

  get potencialCompetencyName(): string {
    return this.potentialCompetency.competencyName;
  }
}

export class StudentCompetencyComparison implements Serializable<StudentCompetencyComparison> {
  competencyID: number;
  competencyName: string;
  competencyScore: number;
  latestExamDate: string;
  growthComment: string;
  lastCompetencyScore: number;
  lastExamDate: string;
  latestGiftCompletedCount: number = 0; // スコア（の受検）が何人から評価を受けてのものか
  latestIsNewScore: boolean = false;    // スコアが新スコアか
  lastGiftCompletedCount: number = 0;   // 前回スコア（の受検）が何人から評価を受けてのものか
  lastIsNewScore: boolean = false;      // 前回スコアが新スコアか

  deserialize(input: any): StudentCompetencyComparison {
    this.competencyID = input.competencyID;
    this.competencyName = input.competencyName;
    this.competencyScore = input.competencyScore;
    this.latestExamDate = input.latestExamDate;
    this.growthComment = input.growthComment;
    this.lastCompetencyScore = input.lastCompetencyScore;
    this.lastExamDate = input.lastExamDate;
    this.latestGiftCompletedCount = input.latestGiftCompletedCount;
    this.latestIsNewScore = input.latestIsNewScore;
    this.lastGiftCompletedCount = input.lastGiftCompletedCount;
    this.lastIsNewScore = input.lastIsNewScore;
    return this;
  }

  get createdAtFormatted(): string {
    return moment(this.latestExamDate).format('YYYY年MM月DD日');
  }

  get lastCreatedAtFormatted(): string {
    return moment(this.lastExamDate).format('YYYY年MM月DD日');
  }

  get isCompetencyScoreLess(): boolean {
    return this.competencyScore < this.lastCompetencyScore;
  }

  get scoreDiff(): number {
    return this.lastCompetencyScore - this.competencyScore;
  }
}

export class StudentCompetencyDistribution implements Serializable<StudentCompetencyDistribution> {
  competencyID: number;
  competencyName: string;
  competencyProposal: string;
  organization: OrganizationCompetencyScoreDistribution;
  overallCompetencyScore: number;
  studentCompetencyScore: number;
  target: TargetCompetencyScore;
  giftCompletedCount: number = 0;
  isNewScore: boolean = false;

  deserialize(input: any): StudentCompetencyDistribution {
    this.competencyID = input.competencyID;
    this.competencyName = input.competencyName;
    this.competencyProposal = input.competencyProposal;
    this.organization = new OrganizationCompetencyScoreDistribution().deserialize(input.organization);
    this.overallCompetencyScore = input.overallCompetencyScore;
    this.studentCompetencyScore = input.studentCompetencyScore;
    this.target = new TargetCompetencyScore().deserialize(input.target);
    this.giftCompletedCount = input.giftCompletedCount;
    this.isNewScore = input.isNewScore;
    return this;
  }

  get isExistScore(): boolean {
    return this.studentCompetencyScore > -1;
  }

  get maxCount(): number {
    return Math.max.apply(null, this.organization.data.map((d: any) => d[2]));
  }
}

export class OrganizationCompetencyScoreDistribution implements Serializable<OrganizationCompetencyScoreDistribution> {
  data: number[];
  name: string;

  deserialize(input: any): OrganizationCompetencyScoreDistribution {
    if (input.data && input.data.length > 0) {
      this.data = input.data.map((d: any) => {
        return [this.percent(d[0]), this.percent(d[1]), d[2]];
      });
    } else {
      this.data = [];
    }

    this.name = input.name;
    return this;
  }

  percent(d: number) {
    return Math.floor(d * 100);
  }
}

export class TargetCompetencyScore implements Serializable<TargetCompetencyScore> {
  data: number;
  name: string;
  lastName: string;
  firstName: string;
  fullName: string;
  giftCompletedCount: number = 0;
  isNewScore: boolean = false;


  deserialize(input: any): TargetCompetencyScore {
    this.data = this.percent(input.data);
    this.name = input.name;
    this.lastName = input.lastName;
    this.firstName = input.firstName;
    this.giftCompletedCount = input.giftCompletedCount;
    this.isNewScore = input.isNewScore;
    // 氏名の設定
    // 現在の言語設定により設定される
    // 言語切り替えにより、currentLang が変化 => currentLang に 名前の順（boolean）を保持している
    if (store.getters.isNameOrder) {
      // 日本語と同じ 姓 + 名
      this.fullName = `${input.lastName} ${input.firstName}`;
    } else {
      // 日本語とは異なる 名 + 姓
      this.fullName = `${input.firstName} ${input.lastName}`;
    }
    return this;
  }

  percent(d: number) {
    return Math.floor(d * 100);
  }
}

export interface StudentCompetencyDataParams {
  studentID: number;
}

export class StudentCompetencyProposal implements Serializable<StudentCompetencyProposal> {
  competencyID: number;
  competencyName: string;
  competencyProposal: string;

  deserialize(input: any): StudentCompetencyProposal {
    this.competencyID = input.competencyID;
    this.competencyName = input.competencyName;
    this.competencyProposal = input.competencyProposal;
    return this;
  }
}
